/**
 * The decision. Fixed to the bottom of the item pane so it is reachable without
 * scrolling back, whatever the item's length.
 *
 * Approve is one keystroke. Reject deliberately is not: it opens a panel that wants a
 * reason code and a real sentence. That asymmetry is the point. The rejection notes are
 * the only signal that tells us which generator prompt is broken, and a one-click reject
 * would throw that signal away.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, XMarkIcon, PencilSquareIcon, FlagIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { ItemDetail, REASON_LABELS, ReviewActionName } from '../../../inbde/reviewTypes';
import { rejectionProblems } from '../../../inbde/reviewRules';
import { Kbd, Eyebrow } from './primitives';

interface Props {
  item: ItemDetail;
  reasonCodes: string[];
  busy: boolean;
  elapsedSeconds: number;
  onAct: (action: ReviewActionName, extra?: { reason_code?: string; notes?: string }) => void;
  onEdit: () => void;
}

const DecisionBar: React.FC<Props> = ({ item, reasonCodes, busy, elapsedSeconds, onAct, onEdit }) => {
  const reduce = useReducedMotion();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const noteRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setRejecting(false); setReason(null); setNotes(''); }, [item.id]);
  useEffect(() => { if (rejecting) noteRef.current?.focus(); }, [rejecting]);

  const problems = rejectionProblems(reason, notes);
  const decided = item.provenance === 'verified' || item.provenance === 'rejected';

  const mins = Math.floor(elapsedSeconds / 60);
  const secs = elapsedSeconds % 60;

  return (
    <div
      className="flex-none"
      style={{ borderTop: '1px solid var(--in-line)', background: 'var(--bg-secondary)' }}
    >
      <AnimatePresence initial={false}>
        {rejecting && (
          <motion.div
            key="reject-panel"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.2, ease: [0.33, 1, 0.68, 1] }}
            style={{ overflow: 'hidden', borderBottom: '1px solid var(--in-line)' }}
          >
            <div className="px-6 py-4 flex flex-col gap-3.5">
              <Eyebrow>What is wrong with it</Eyebrow>
              <div className="flex flex-wrap gap-1.5">
                {reasonCodes.map((code) => {
                  const active = reason === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setReason(code)}
                      aria-pressed={active}
                      className="in-focus text-[12px] font-medium px-2.5 py-1.5"
                      style={{
                        borderRadius: 'var(--in-r-control)',
                        border: `1px solid ${active ? 'var(--in-incorrect)' : 'var(--in-line-strong)'}`,
                        background: active ? 'var(--in-incorrect-bg)' : 'var(--bg-secondary)',
                        color: active ? 'var(--in-incorrect)' : 'var(--text-secondary)',
                      }}
                    >
                      {REASON_LABELS[code] || code}
                    </button>
                  );
                })}
              </div>

              <textarea
                ref={noteRef}
                className="in-textarea in-focus"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="The keyed answer treats percussion tenderness as diagnostic of the pulpal state."
                aria-label="What is wrong with this item"
              />

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="in-btn in-btn--reject in-focus"
                  disabled={problems.length > 0 || busy}
                  onClick={() => onAct('reject', { reason_code: reason!, notes: notes.trim() })}
                >
                  <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                  Reject this item
                </button>
                <button type="button" className="in-btn in-focus" onClick={() => setRejecting(false)} disabled={busy}>
                  Cancel
                </button>
                <span className="text-[11.5px] ml-auto" style={{ color: problems.length ? 'var(--text-tertiary)' : 'var(--in-correct)' }}>
                  {problems.length > 0
                    ? problems[0]
                    : `Ready. ${notes.trim().length} characters.`}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="px-6 py-3.5 flex items-center gap-2.5 flex-wrap">
        {decided ? (
          <>
            <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              {item.provenance === 'verified' ? 'You approved this item.' : 'This item is rejected.'}
            </span>
            <button type="button" className="in-btn in-focus" onClick={() => onAct('reopen')} disabled={busy}>
              <ArrowUturnLeftIcon className="w-4 h-4" aria-hidden="true" />
              Reopen
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="in-btn in-btn--approve in-focus"
              onClick={() => onAct('approve')}
              disabled={busy || rejecting}
            >
              <CheckIcon className="w-4 h-4" aria-hidden="true" />
              Approve
              <Kbd>A</Kbd>
            </button>

            <button
              type="button"
              className="in-btn in-btn--reject in-focus"
              onClick={() => setRejecting(true)}
              disabled={busy || rejecting}
            >
              <XMarkIcon className="w-4 h-4" aria-hidden="true" />
              Reject
              <Kbd>R</Kbd>
            </button>

            <button type="button" className="in-btn in-focus" onClick={onEdit} disabled={busy}>
              <PencilSquareIcon className="w-4 h-4" aria-hidden="true" />
              Edit
              <Kbd>E</Kbd>
            </button>
          </>
        )}

        <button
          type="button"
          className="in-btn in-focus"
          onClick={() => onAct(item.flagged ? 'unflag' : 'flag')}
          disabled={busy}
          style={item.flagged ? { borderColor: 'var(--in-flagged)', color: 'var(--in-flagged)' } : undefined}
        >
          <FlagIcon className="w-4 h-4" aria-hidden="true" />
          {item.flagged ? 'Clear flag' : 'Flag to discuss'}
          <Kbd>F</Kbd>
        </button>

        <div className="ml-auto flex items-center gap-4">
          <span className="in-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }} title="Time on this item">
            {mins}:{String(secs).padStart(2, '0')}
          </span>
          <span className="text-[11.5px] hidden md:inline" style={{ color: 'var(--text-tertiary)' }}>
            <Kbd>J</Kbd> next, <Kbd>K</Kbd> previous
          </span>
        </div>
      </div>
    </div>
  );
};

export default DecisionBar;
